import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLoad } from "@/lib/hooks";
import { fmt, fdate } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { PageHeader, Section, StatusBadge, NativeSelect } from "@/components/common";
import { ActivityDialog } from "@/components/crm/ActivityDialog";
import { ActivityTimeline } from "@/components/crm/ActivityTimeline";
import { useStageMove } from "@/components/crm/StageMove";

const SALES = ["tenant_admin", "accountant", "sales"];

function Linked({ title, rows, to, testId, render }) {
  return (
    <Section title={`${title} (${rows.length})`} testId={testId}>
      <ul className="divide-y divide-slate-100">
        {rows.length === 0 && <li className="px-5 py-4 text-sm text-slate-400">None linked yet.</li>}
        {rows.map((r) => <li key={r.id}><Link to={`${to}/${r.id}`} data-testid={`${testId}-${r.number}`} className="flex items-center justify-between gap-3 px-5 py-3 text-sm hover:bg-slate-50">{render(r)}</Link></li>)}
      </ul>
    </Section>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const [d, reload] = useLoad(`/crm/opportunities/${id}`);
  const [act, setAct] = useState(false);
  const { canWrite, currency } = useAccess();
  const can = canWrite(SALES);
  const { move, dialog } = useStageMove(reload);
  if (!d) return null;
  const o = d.opportunity;
  return (
    <>
      <Link to="/app/crm" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="opp-back"><ArrowLeft className="h-4 w-4" />CRM</Link>
      <PageHeader title={o.title} subtitle={`${o.customer_name || "No customer"} · ${fmt(o.value, currency)} · owner ${o.owner}${o.expected_close ? ` · close ${fdate(o.expected_close)}` : ""}`}>
        {can ? <NativeSelect testId="opp-detail-stage" className="self-center" value={o.stage} onChange={(v) => move(o.id, v)} options={d.stages.map((s) => ({ value: s.code, label: s.name }))} />
          : <StatusBadge status="pending" label={o.stage} className="self-center" />}
      </PageHeader>
      <div className="grid gap-4 text-sm sm:grid-cols-4">
        {[["Service interest", o.service_interest], ["Quantity", o.quantity], ["Due date", o.due_date && fdate(o.due_date)], ["Lead", d.lead && <Link className="text-[#389F95]" to={`/app/crm/leads/${d.lead.id}`} data-testid="opp-lead-link">{d.lead.name}</Link>]].map(([k, v]) => (
          <div key={k} className="po-card p-4"><div className="po-label">{k}</div><div className="mt-1 text-slate-900">{v || "—"}</div></div>))}
      </div>
      <p className="text-xs text-slate-500" data-testid="opp-on-won-hint">On Won: {d.on_won === "job" ? "a draft job is created" : "a draft quote is created (or you can link an existing one)"}.</p>
      <div className="grid gap-6 lg:grid-cols-2">
        <Linked title="Quotes" rows={d.quotes} to="/app/quotes" testId="opp-quotes" render={(q) => <><span className="font-medium text-[#13294B]">{q.number} · {q.title}</span><StatusBadge status={q.status} /><span className="tabular-nums">{fmt(q.totals?.total, currency)}</span></>} />
        <Linked title="Jobs" rows={d.jobs} to="/app/jobs" testId="opp-jobs" render={(j) => <><span className="font-medium text-[#13294B]">{j.number} · {j.title}</span>{j.draft ? <StatusBadge status="draft" label="Draft" /> : <StatusBadge status={j.status} />}</>} />
      </div>
      <Section title="Activity timeline" testId="opp-activities" action={can && <Button size="sm" data-testid="opp-log-activity" onClick={() => setAct(true)}><Plus className="mr-1 h-4 w-4" />Log activity</Button>}>
        <ActivityTimeline rows={d.activities} reload={reload} can={can} testId="opp-activity-timeline" />
      </Section>
      {o.notes && <Section title="Notes"><p className="whitespace-pre-wrap p-5 text-sm text-slate-700">{o.notes}</p></Section>}
      <ActivityDialog open={act} onClose={() => setAct(false)} onSaved={reload} target={{ opportunity_id: id }} />
      {dialog}
    </>
  );
}
